import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const INSURANCE_AMOUNTS = [10, 25, 50, 100];

export default function CopySettingsPage() {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [settings, setSettings] = useState({ lot_size_mode: 'scale', fixed_lot: 0.01, risk_multiplier: 1.0, max_daily_loss: 100.0, insurance_active: false, insurance_amount: 50 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedInsuranceAmt, setSelectedInsuranceAmt] = useState(50);

  useEffect(() => {
    const load = async () => {
      try {
        const [subs, ins] = await Promise.all([
          api.getFollowerSubscriptions('advanced').catch(() => []),
          api.getInsuranceStatus(subscriptionId).catch(() => null),
        ]);
        const sub = (subs || []).find(s => s.subscription_id === subscriptionId);
        setSubscription(sub);
        if (sub?.copy_settings) setSettings(prev => ({ ...prev, ...sub.copy_settings }));
        setInsurance(ins);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [subscriptionId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Coerce raw text inputs to numbers right before sending to the backend.
      // Empty fields fall back to sensible defaults so the schema stays valid.
      const payload = {
        ...settings,
        fixed_lot:      (() => { const n = parseFloat(settings.fixed_lot); return Number.isFinite(n) && n > 0 ? n : 0.01; })(),
        risk_multiplier:(() => { const n = parseFloat(settings.risk_multiplier); return Number.isFinite(n) && n > 0 ? n : 1.0; })(),
        max_daily_loss: (() => { const n = parseFloat(settings.max_daily_loss); return Number.isFinite(n) && n > 0 ? n : 100; })(),
      };
      await api.updateCopySettings(subscriptionId, payload);
      toast.success('Settings saved!');
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const handleActivateInsurance = async () => {
    try {
      await api.activateInsurance(subscriptionId, selectedInsuranceAmt);
      toast.success(`Insurance activated! Covered up to $${selectedInsuranceAmt}`);
      const ins = await api.getInsuranceStatus(subscriptionId);
      setInsurance(ins);
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}><div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /></div>;

  const INPUT = { width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '10px 12px', fontSize: '14px', outline: 'none', borderRadius: 0 };
  const LABEL = { display: 'block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--pp-text-3)', marginBottom: '6px' };

  return (
    <div className="page-container">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
        <button onClick={() => navigate('/copy-dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>COPY SETTINGS</h1>
      </div>

      <div style={{ padding: 20 }}>
        {subscription && (
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 20 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>{subscription.strategy_name}</p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{subscription.instrument}</p>
          </div>
        )}

        {/* Lot Size Settings */}
        <p className="section-label" style={{ marginBottom: 12 }}>Copy Settings</p>

        <div style={{ marginBottom: 16 }}>
          <label style={LABEL}>Lot Size Mode</label>
          <select style={{ ...INPUT, cursor: 'pointer' }} value={settings.lot_size_mode} onChange={e => setSettings(prev => ({ ...prev, lot_size_mode: e.target.value }))}>
            <option value="scale">Equity Ratio (Recommended)</option>
            <option value="fixed">Fixed Lot Size</option>
          </select>
          <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
            {settings.lot_size_mode === 'scale'
              ? 'Scales each trade proportionally to your account balance vs. the master account. Best for safety across any account size.'
              : 'Every trade uses the exact fixed lot size you set below, regardless of account balance.'}
          </p>
        </div>

        {settings.lot_size_mode === 'fixed' && (
          <div style={{ marginBottom: 16 }}>
            <label style={LABEL}>Fixed Lot Size</label>
            <input
              data-testid="copy-fixed-lot-input"
              style={INPUT}
              type="number"
              step="0.01"
              min="0.01"
              value={settings.fixed_lot ?? ''}
              onChange={e => {
                const v = e.target.value;
                setSettings(prev => ({ ...prev, fixed_lot: v === '' ? '' : v }));
              }}
              onBlur={e => {
                // Normalise on blur — only snap to a default if truly empty
                const n = parseFloat(e.target.value);
                setSettings(prev => ({ ...prev, fixed_lot: Number.isFinite(n) && n > 0 ? n : 0.01 }));
              }}
            />
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={LABEL}>Risk Multiplier</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[0.1, 0.25, 0.5, 0.75, 1.0, 1.5, 2.0].map(m => (
              <button
                key={m}
                data-testid={`copy-risk-mult-${m}`}
                onClick={() => setSettings(prev => ({ ...prev, risk_multiplier: m }))}
                style={{ flex: '1 1 calc(25% - 6px)', minWidth: 56, padding: '10px 0', background: settings.risk_multiplier === m ? 'var(--pp-primary)' : 'transparent', border: `1px solid ${settings.risk_multiplier === m ? 'var(--pp-primary)' : 'var(--pp-border)'}`, color: settings.risk_multiplier === m ? '#fff' : 'var(--pp-text-2)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                {m}x
              </button>
            ))}
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>0.1x = scale every trade to 10% of master's risk. 1.0x mirrors the master exactly.</p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={LABEL}>Max Daily Loss ($)</label>
          <input
            data-testid="copy-max-daily-loss-input"
            style={INPUT}
            type="number"
            value={settings.max_daily_loss ?? ''}
            onChange={e => {
              const v = e.target.value;
              setSettings(prev => ({ ...prev, max_daily_loss: v === '' ? '' : v }));
            }}
            onBlur={e => {
              const n = parseFloat(e.target.value);
              setSettings(prev => ({ ...prev, max_daily_loss: Number.isFinite(n) && n > 0 ? n : 100 }));
            }}
          />
        </div>

        <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ width: '100%', height: 52, marginBottom: 24 }}>
          {saving ? 'SAVING...' : 'SAVE SETTINGS'}
        </button>

        {/* Insurance */}
        <p className="section-label" style={{ marginBottom: 12 }}>Copy-Trade Insurance</p>

        {insurance?.active ? (
          <div style={{ background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.25)', padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Shield size={20} color="var(--pp-success)" />
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: 'var(--pp-success)' }}>Protected up to ${insurance.amount}</p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', marginTop: 2 }}>Expires: {insurance.expires_at?.slice(0, 10)}</p>
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--pp-text-2)', margin: 0, lineHeight: 1.6 }}>Your losses are covered up to ${insurance.amount} for this 30-day cycle. If you end negative, you'll be reimbursed up to your coverage amount.</p>
          </div>
        ) : (
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Shield size={20} color="var(--pp-text-3)" />
              <p style={{ margin: 0, fontWeight: 600 }}>Activate Insurance</p>
            </div>
            <p style={{ fontSize: 13, color: 'var(--pp-text-2)', marginBottom: 16, lineHeight: 1.6 }}>Protect your copies! If you end a 30-day cycle negative, you'll be reimbursed up to your coverage amount.</p>
            <p className="section-label" style={{ marginBottom: 8 }}>Coverage Amount</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {INSURANCE_AMOUNTS.map(a => (
                <button key={a} onClick={() => setSelectedInsuranceAmt(a)} style={{ flex: 1, padding: '8px 0', background: selectedInsuranceAmt === a ? 'var(--pp-accent)' : 'transparent', border: `1px solid ${selectedInsuranceAmt === a ? 'var(--pp-accent)' : 'var(--pp-border)'}`, color: selectedInsuranceAmt === a ? '#000' : 'var(--pp-text-2)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>${a}</button>
              ))}
            </div>
            <button onClick={handleActivateInsurance} style={{ width: '100%', height: 48, background: 'rgba(0,255,148,0.12)', border: '1px solid rgba(0,255,148,0.3)', color: 'var(--pp-success)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Shield size={16} /> ACTIVATE ${selectedInsuranceAmt} INSURANCE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
